import { BooleanField, BooleanInput, InPlaceEditorProps, Labeled, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";

/**
 * In-place boolean editor for use in editable grids or detail views.
 *
 * Renders a boolean value with the ability to toggle it via a {@link BooleanInput} when activated.
 * Wrapped in a {@link Labeled} component to display the provided label.
 *
 * @param source - The name of the field to bind to
 * @param label - Optional field label
 * @param readOnly - If true, disables editing and shows display only
 * @param rest - Additional props forwarded to the InPlaceEditor and BooleanInput
 */
const InPlaceBooleanEditor = ({
  source,
  label,
  readOnly = false,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Read-only mode: just show the display component
  if (readOnly) {
    return (
      <Labeled label={label}>
        <BooleanField source={source} variant="body1" sx={{ display: "block", mb: 0.5 }} emptyText="-" />
      </Labeled>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <BooleanInput
            {...rest}
            source={source}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={validate ? undefined : false}
            validate={validate}
            autoFocus
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <BooleanField source={source} variant="body1" sx={{ display: "block", mb: 0.5 }} emptyText="-" />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceBooleanEditor;
